import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Table from "react-bootstrap/Table";
import AxisSpinner from "./AxisSpinner";
import { BsCalendarCheck, BsPersonCheck, BsSearch, BsDownload, BsSortUp, BsSortDown } from "react-icons/bs";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { format, differenceInWeeks, differenceInDays } from "date-fns";
import XLSX from "xlsx";

const NurseDateLastWorkedReportTable = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("lastWorkedDate");
  const [sortDirection, setSortDirection] = useState("asc");
  const [timeFilter, setTimeFilter] = useState("all");
  const searchRef = useRef();
  const tableRef = useRef();

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm, timeFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await authAxios().get(
        "/api/backoffice/reporting/nurse_date_last_worked",
      );
      setReports(response.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching nurse date last worked reports:", err);
      setReports([]);
      setLoading(false);
    }
  };

  const getTimePeriod = (timestamp) => {
    if (!timestamp) return "never";
    
    const lastWorkedDate = new Date(timestamp);
    const now = new Date();
    const daysDiff = differenceInDays(now, lastWorkedDate);
    const weeksDiff = differenceInWeeks(now, lastWorkedDate);
    
    if (daysDiff > 30) return "over-month";
    if (weeksDiff > 2) return "over-two-weeks";
    return "within-two-weeks";
  };

  const filterReports = () => {
    let filtered = reports;

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter((report) => {
        const fullName =
          `${report.user.forename} ${report.user.surname}`.toLowerCase();
        const email = report.user.email.toLowerCase();
        const search = searchTerm.toLowerCase();

        return fullName.includes(search) || email.includes(search);
      });
    }

    // Apply time period filter
    if (timeFilter !== "all") {
      filtered = filtered.filter((report) => {
        return getTimePeriod(report.lastWorkedDate) === timeFilter;
      });
    }

    setFilteredReports(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTimeFilterChange = (e) => {
    setTimeFilter(e.target.value);
  };

  const handleRowClick = (row) => {
    navigate(`/users/${row?.user?.id}`);
  };

  const exportTable = async () => {
    const wb = XLSX.utils.table_to_book(tableRef.current);
    XLSX.writeFile(wb, "NurseDateLastWorkedReport.xlsx");
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? <BsSortUp className="ms-1" /> : <BsSortDown className="ms-1" />;
  };

  const formatLastWorkedDate = (timestamp) => {
    if (!timestamp) {
      return <Badge bg="warning">Never worked</Badge>;
    }
    
    const lastWorkedDate = new Date(timestamp);
    const now = new Date();
    const daysDiff = differenceInDays(now, lastWorkedDate);
    const weeksDiff = differenceInWeeks(now, lastWorkedDate);
    
    let badgeColor = "success"; // Green for within 2 weeks
    if (daysDiff > 30) {
      badgeColor = "danger"; // Red for over a month
    } else if (weeksDiff > 2) {
      badgeColor = "warning"; // Amber for over 2 weeks
    }
    
    return (
      <Badge bg={badgeColor}>
        {format(lastWorkedDate, "yyyy-MM-dd HH:mm")}
      </Badge>
    );
  };

  const getUserStatusBadge = (status) => {
    const statusColors = {
      ACTIVE: "success",
      ONHOLD: "warning",
      BLOCKED: "danger",
    };
    return <Badge bg={statusColors[status] || "secondary"}>{status}</Badge>;
  };

  const sortedReports = filteredReports.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortColumn) {
      case "name":
        aValue = `${a.user.forename} ${a.user.surname}`.toLowerCase();
        bValue = `${b.user.forename} ${b.user.surname}`.toLowerCase();
        break;
      case "email":
        aValue = a.user.email.toLowerCase();
        bValue = b.user.email.toLowerCase();
        break;
      case "status":
        aValue = a.user.status;
        bValue = b.user.status;
        break;
      case "lastWorkedDate":
      default:
        // For date sorting, treat null as oldest (earliest) date
        if (!a.lastWorkedDate && !b.lastWorkedDate) return 0;
        if (!a.lastWorkedDate) return sortDirection === "asc" ? -1 : 1;
        if (!b.lastWorkedDate) return sortDirection === "asc" ? 1 : -1;
        aValue = new Date(a.lastWorkedDate);
        bValue = new Date(b.lastWorkedDate);
        break;
    }
    
    if (sortColumn === "lastWorkedDate") {
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    }
    
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Container fluid>
      <Card className="mb-4">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">
                <BsPersonCheck className="me-2" />
                Nurse Date Last Worked Report
              </h5>
            </Col>
            <Col xs="auto">
              <Button variant="outline-secondary" size="sm" onClick={() => exportTable()}>
                <BsDownload className="me-2" />
                Export
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <BsSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by nurse name or email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Col>
            <Col md={4}>
              <Form.Select value={timeFilter} onChange={handleTimeFilterChange}>
                <option value="all">All Time Periods</option>
                <option value="never">Never Worked</option>
                <option value="within-two-weeks">Within 2 Weeks</option>
                <option value="over-two-weeks">Over 2 Weeks</option>
                <option value="over-month">Over 1 Month</option>
              </Form.Select>
            </Col>
            <Col md={4} className="text-end">
              <small className="text-muted">
                Showing {filteredReports.length} of {reports.length} nurse
                {reports.length !== 1 ? "s" : ""}
              </small>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <AxisSpinner loading={loading} />
            </div>
          ) : reports.length === 0 ? (
            <Alert variant="info">
              <BsCalendarCheck className="me-2" />
              No nurse data is available.
            </Alert>
          ) : filteredReports.length === 0 ? (
            <Alert variant="warning">
              <BsSearch className="me-2" />
              No nurses match your search criteria.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover striped className="mb-0" ref={tableRef}>
                <thead>
                  <tr>
                    <th 
                      onClick={() => handleSort("name")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                      title="Click to sort by name"
                    >
                      Nurse Name{getSortIcon("name")}
                    </th>
                    <th 
                      onClick={() => handleSort("email")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                      title="Click to sort by email"
                    >
                      Email{getSortIcon("email")}
                    </th>
                    <th 
                      onClick={() => handleSort("status")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                      title="Click to sort by status"
                    >
                      Status{getSortIcon("status")}
                    </th>
                    <th 
                      onClick={() => handleSort("lastWorkedDate")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                      title="Click to sort by last worked date"
                    >
                      Last Worked{getSortIcon("lastWorkedDate")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedReports.map((report, index) => (
                    <tr
                      key={index}
                      onClick={() => handleRowClick(report)}
                      style={{ cursor: "pointer" }}
                      title="Click to view nurse profile"
                    >
                      <td>
                        <strong>
                          {report.user.forename} {report.user.surname}
                        </strong>
                      </td>
                      <td>{report.user.email}</td>
                      <td>{getUserStatusBadge(report.user.status)}</td>
                      <td>{formatLastWorkedDate(report.lastWorkedDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default NurseDateLastWorkedReportTable;
